import { useState } from "react";
import { toast } from "sonner";
import { ShieldCheck, KeyRound, Loader2, Lock } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import AppNav from "@/components/AppNav";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function Settings() {
  const { user, refreshUser } = useAuth();
  const [saving, setSaving] = useState(false);
  const [pwSending, setPwSending] = useState(false);
  const [pwSent, setPwSent] = useState(null);
  const [showEmailReset, setShowEmailReset] = useState(false);
  const [cp, setCp] = useState({ current_password: "", new_password: "", confirm: "" });
  const [cpLoading, setCpLoading] = useState(false);
  const [cpError, setCpError] = useState("");

  const setCpField = (k) => (e) => setCp({ ...cp, [k]: e.target.value });

  const changePassword = async (e) => {
    e.preventDefault();
    setCpError("");
    if (cp.new_password !== cp.confirm) { setCpError("Le password non coincidono."); return; }
    if (cp.new_password.length < 8 || !/[a-z]/.test(cp.new_password) || !/[A-Z]/.test(cp.new_password) || !/\d/.test(cp.new_password)) {
      setCpError("La nuova password deve avere almeno 8 caratteri, con maiuscola, minuscola e un numero.");
      return;
    }
    setCpLoading(true);
    try {
      await api.post("/auth/change-password", { current_password: cp.current_password, new_password: cp.new_password });
      setCp({ current_password: "", new_password: "", confirm: "" });
      toast.success("Password aggiornata con successo");
    } catch (err) {
      setCpError(formatApiErrorDetail(err.response?.data?.detail) || "Errore");
    } finally {
      setCpLoading(false);
    }
  };

  const toggle2fa = async (checked) => {
    setSaving(true);
    try {
      await api.post(checked ? "/auth/2fa/enable" : "/auth/2fa/disable");
      await refreshUser();
      toast.success(checked ? "Autenticazione a due fattori attivata" : "Autenticazione a due fattori disattivata");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Errore");
    } finally {
      setSaving(false);
    }
  };

  const sendReset = async () => {
    setPwSending(true);
    try {
      const { data } = await api.post("/auth/forgot-password", { email: user.email });
      setPwSent(data);
      toast.success("Email di reset inviata");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Errore");
    } finally {
      setPwSending(false);
    }
  };

  return (
    <div className="flex bg-[#0D0D0D] min-h-screen text-white">
      <AppNav />
      <main className="flex-1 p-5 sm:p-8 pb-24 md:pb-8 overflow-x-hidden">
        <div className="max-w-2xl mx-auto">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-12 h-12 rounded-2xl bg-[#E91E8C]/15 flex items-center justify-center">
              <Lock className="w-6 h-6 text-[#E91E8C]" />
            </div>
            <div>
              <h1 className="text-2xl font-black font-heading">Sicurezza account</h1>
              <p className="text-white/50 text-sm">Proteggi il tuo account Luvme</p>
            </div>
          </div>

          {/* 2FA */}
          <div className="bg-[#1A1A1A] border border-white/10 rounded-3xl p-5 sm:p-6 mb-5" data-testid="twofa-card">
            <div className="flex items-start gap-4">
              <ShieldCheck className="w-6 h-6 text-[#E91E8C] shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-4">
                  <h2 className="font-bold font-heading">Autenticazione a due fattori</h2>
                  <Switch
                    checked={!!user?.twofa_enabled}
                    disabled={saving}
                    onCheckedChange={toggle2fa}
                    data-testid="twofa-switch"
                    className="data-[state=checked]:bg-[#E91E8C]"
                  />
                </div>
                <p className="text-sm text-white/60 mt-1.5">
                  Al login ti chiederemo un codice a 6 cifre inviato via email. Un livello di protezione extra contro i furti di account.
                </p>
                {user?.twofa_enabled && (
                  <span className="inline-block mt-3 text-xs font-semibold text-green-400 bg-green-400/10 border border-green-400/20 rounded-full px-3 py-1" data-testid="twofa-status-on">
                    Attiva
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Change password (in-app) */}
          <div className="bg-[#1A1A1A] border border-white/10 rounded-3xl p-5 sm:p-6" data-testid="password-card">
            <div className="flex items-start gap-4">
              <KeyRound className="w-6 h-6 text-[#F5C6D6] shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <h2 className="font-bold font-heading">Cambia password</h2>
                <p className="text-sm text-white/60 mt-1.5 mb-4">
                  Inserisci la password attuale e scegline una nuova. Nessuna email necessaria.
                </p>
                <form onSubmit={changePassword} className="space-y-3" data-testid="change-password-form">
                  <div>
                    <Label className="text-white/70 text-sm">Password attuale</Label>
                    <Input
                      type="password" required value={cp.current_password} onChange={setCpField("current_password")} data-testid="cp-current"
                      className="mt-1.5 bg-[#0D0D0D] border-white/10 h-11 rounded-xl focus:border-[#E91E8C] focus-visible:ring-[#E91E8C]"
                      placeholder="••••••••"
                    />
                  </div>
                  <div>
                    <Label className="text-white/70 text-sm">Nuova password</Label>
                    <Input
                      type="password" required minLength={8} value={cp.new_password} onChange={setCpField("new_password")} data-testid="cp-new"
                      className="mt-1.5 bg-[#0D0D0D] border-white/10 h-11 rounded-xl focus:border-[#E91E8C] focus-visible:ring-[#E91E8C]"
                      placeholder="Min 8 caratteri, maiuscola, minuscola e numero"
                    />
                  </div>
                  <div>
                    <Label className="text-white/70 text-sm">Conferma nuova password</Label>
                    <Input
                      type="password" required value={cp.confirm} onChange={setCpField("confirm")} data-testid="cp-confirm"
                      className="mt-1.5 bg-[#0D0D0D] border-white/10 h-11 rounded-xl focus:border-[#E91E8C] focus-visible:ring-[#E91E8C]"
                      placeholder="Ripeti la nuova password"
                    />
                  </div>
                  {cpError && <p className="text-sm text-red-400" data-testid="cp-error">{cpError}</p>}
                  <Button
                    type="submit" disabled={cpLoading} data-testid="cp-submit"
                    className="rounded-xl bg-[#E91E8C] hover:bg-[#D8157B] text-white font-semibold hover:shadow-[0_0_18px_rgba(233,30,140,0.5)] transition-all"
                  >
                    {cpLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Aggiorna password"}
                  </Button>
                </form>

                <div className="mt-4 pt-4 border-t border-white/10">
                  {!showEmailReset && !pwSent ? (
                    <button onClick={() => setShowEmailReset(true)} data-testid="show-email-reset" className="text-xs text-white/50 hover:text-white transition-colors">
                      Hai dimenticato la password attuale? Reimpostala via email
                    </button>
                  ) : pwSent?.demo_reset_link ? (
                    <div className="bg-amber-400/10 border border-amber-400/20 rounded-xl p-3" data-testid="settings-demo-reset">
                      <p className="text-xs text-amber-300/90 mb-2">Modalità demo — apri il link per reimpostare:</p>
                      <a href={pwSent.demo_reset_link} className="text-[#E91E8C] text-sm break-all hover:underline">{pwSent.demo_reset_link}</a>
                    </div>
                  ) : (
                    <Button
                      onClick={sendReset} disabled={pwSending} data-testid="change-password-btn"
                      className="rounded-xl bg-white/10 hover:bg-white/15 text-white font-semibold text-sm h-9"
                    >
                      {pwSending ? <Loader2 className="w-4 h-4 animate-spin" /> : "Invia link di reset alla mia email"}
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
